import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, takeEvery } from 'redux-saga/effects';

import { apiGetBrandingInfo } from 'src/api/branding';
import { apiCanCreateCampaignFromTemplate, apiCheckExpiredCampaigns } from 'src/api/campaigns';
import { apiGetCountries, apiGetCurrencyList, apiGetRegionsByCountryId, apiGetTimezones } from 'src/api/lookup';
import { FrontConfigurationType } from 'src/models/FrontendConfiguration';
import { CurrencyType, LookupCountryType } from 'src/models/Lookup';
import { httpResponseCheckSaga } from 'src/store/commonSaga';
import { commonSlice } from 'src/store/commonPart/slice';
import { apiGetConfiguration, apiGetPartnerConfiguration, apiGetRecaptchaConfiguration } from 'src/api/configuration';
import { apiGetReleaseToggleEnabled } from 'src/api/releaseToggle';
import { store } from 'src/store/store';
import { apiCheckCustomerType, apiSetPersonalOrBusinessForBusinessModalShown } from 'src/api/customerTypeSecurity';
import { dispatchShowPersonalOrBusinessForBusinessModal } from 'src/store/commonPart/index';
import { getAndClearLocalStorage } from 'src/utils/localStorage';
import { apiGetExperimentalToggleEnabled } from 'src/api/featureToggle';

export function* getConfigurationSaga(): Generator {
  yield httpResponseCheckSaga(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    function* () {
      return yield all([call(apiGetConfiguration), call(apiGetRecaptchaConfiguration)]);
    },
    function* (response) {
      yield put(
        commonSlice.actions.getConfigurationOk({
          ...(response as [FrontConfigurationType, { SiteKey: string }])[0],
          RecaptchaSiteKey: (response as [FrontConfigurationType, { SiteKey: string }])[1].SiteKey,
        })
      );
    },
    commonSlice.actions.getConfigurationError
  );
}

export const currencyPrepareToStore = (item: CurrencyType): CurrencyType => {
  const hasCode = item.Name.match(/\((.*)\)/);
  return { ...item, Symbol: item.Symbol.trim(), code: hasCode ? hasCode[1] : null };
};

export function* getCurrenciesSaga(): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiGetCurrencyList);
    },
    function* (response) {
      yield put(commonSlice.actions.getCurrencyListOk(response.map(currencyPrepareToStore)));
    },
    commonSlice.actions.getCurrencyListError
  );
}

export function* getReleaseFeatureFlagsSaga(): Generator {
  const list: string[] = store.getState().common.releaseFeatureFlags.map(({ name }) => name);

  if (list.length) {
    yield httpResponseCheckSaga(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      function* () {
        // it's OK
        return yield all(list.map((str) => call(apiGetReleaseToggleEnabled, str)));
      },
      function* (response: boolean[]) {
        yield put(
          commonSlice.actions.getReleaseFeatureFlagsOk(
            response.map((bool, index) => ({
              name: list[index],
              response: bool,
            }))
          )
        );
      },
      commonSlice.actions.getReleaseFeatureFlagsError
    );
  }
}

export function* getExperimentalFeatureFlagsSaga(): Generator {
  const list: string[] = store.getState().common.experimentalFeatureFlags.map(({ name }) => name);

  if (list.length) {
    yield httpResponseCheckSaga(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      function* () {
        // it's OK
        return yield all(list.map((str) => call(apiGetExperimentalToggleEnabled, str)));
      },
      function* (response: boolean[]) {
        yield put(
          commonSlice.actions.getExperimentalFeatureFlagsOk(
            response.map((bool, index) => ({
              name: list[index],
              response: bool,
            }))
          )
        );
      },
      commonSlice.actions.getExperimentalFeatureError
    );
  }
}

export function* getCurrentPartnerSaga(): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiGetPartnerConfiguration);
    },
    function* (response) {
      /** request release featureflags here */
      yield call(getReleaseFeatureFlagsSaga);
      yield call(getExperimentalFeatureFlagsSaga);

      yield put(commonSlice.actions.getCurrentPartnerOk(response));
    },
    commonSlice.actions.getCurrentPartnerError
  );
}

export function* getBrandingModelSaga(): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiGetBrandingInfo);
    },
    function* (response) {
      yield put(commonSlice.actions.getBrandingModelOk(response));
    },
    commonSlice.actions.getBrandingModelError
  );
}

export function* getCountriesSaga(): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiGetCountries);
    },
    function* (response) {
      yield put(commonSlice.actions.getCountriesListOk(response));
    },
    commonSlice.actions.getCountriesListError
  );
}

export function* getTimezonesSaga(): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiGetTimezones);
    },
    function* (response) {
      yield put(commonSlice.actions.getTimezonesListOk(response));
    },
    commonSlice.actions.getTimezonesListError
  );
}

export function* getRegionsByCountryIdSaga({ payload }: PayloadAction<LookupCountryType['ID']>): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiGetRegionsByCountryId, payload);
    },
    function* (response) {
      yield put(commonSlice.actions.getRegionsByCountryIdOk({ data: response, countryId: payload }));
    },
    commonSlice.actions.getRegionsByCountryIdError
  );
}

export function* checkCanCreateCampaignFromTemplate(): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiCanCreateCampaignFromTemplate);
    },
    function* (response) {
      yield put(commonSlice.actions.canCreateCampaignFromTemplateOk(response));
    },
    commonSlice.actions.canCreateCampaignFromTemplateError
  );
}

export function* checkCustomerType(): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiCheckCustomerType);
    },
    function* (response) {
      yield put(commonSlice.actions.checkCustomerTypeOk(response.ShouldRedirectToStopPage));

      if (response.ShouldShowPersonalOrBusinessForBusinessModal) {
        yield call(dispatchShowPersonalOrBusinessForBusinessModal);
        yield call(apiSetPersonalOrBusinessForBusinessModalShown);
      }
    },
    commonSlice.actions.checkCustomerTypeError
  );
}

export function* checkExpiredCampaigns(): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiCheckExpiredCampaigns);
    },
    function* (response) {
      yield put(commonSlice.actions.showExpiredCampaignsModal(response));
    },
    commonSlice.actions.hideExpiredCampaignsModal
  );
}

export function* commonSagas(): Generator {
  yield takeEvery(commonSlice.actions.getCountriesListStart, getCountriesSaga);
  yield takeEvery(commonSlice.actions.getRegionsByCountryIdStart, getRegionsByCountryIdSaga);

  yield takeEvery(commonSlice.actions.getTimezonesListStart, getTimezonesSaga);

  yield takeEvery(commonSlice.actions.checkExpiredCampaignsStart, checkExpiredCampaigns);

  if (getAndClearLocalStorage('checkExpiredCampaignsModal') === 'true') {
    store.dispatch(commonSlice.actions.checkExpiredCampaignsStart());
  }
}
